import { AnalyticsService } from "mis-crystal-design-system/analytics";
/** @format */

import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { ToastService } from "mis-crystal-design-system/toast";
import { Moment, parseZone, tz } from "moment";
import { combineLatest, config, Observable, of, Subject, Subscription, throwError } from "rxjs";
import { catchError, debounceTime, last, map, skip, take, takeLast, takeUntil } from "rxjs/operators";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../../constants/assets.constants";
import { NsWISConfigurations } from "../../../models/configurations.model";
import { NsEmpWebExpData } from "../../../models/emp-web-exp.model";
import { ApiService } from "../../../services/api.service";
import { BookingDataService } from "../../../services/booking-data.service";
import { CommuteService } from "../../../services/commute.service";
import { createBookingRequest, editBookingRequest } from "./booking-generators";
import { SeatService } from "../../../services/seat.service";
import { PreferenceDataService } from "../../../services/preference-data.service";
import { MealService } from "@mis-wis/meal";
import {
  bookingReasonValidator,
  getAdditionalBookings,
  labelForShifts,
  parkingValidator,
  recurrenceValidator
} from "../../../functions/booking-utils.functions";
import { ConfigurationService } from "../../../services/configuration.service";
import { AdhocShiftsComponent } from "../../modals/adhoc-shifts/adhoc-shifts.component";
import { ModalService } from "mis-design-system/mis-modal";
import { IDatePickerConfig, IDatePickerToastText } from "mis-crystal-design-system/datepicker_v2";
import { RecurrenceConfirmationComponent } from "../../modals/recurrence-confirmation/recurrence-confirmation.component";
import { TranslateService } from "@ngx-translate/core";
import { EmpUserService } from "../../../services/emp-user.service";
import * as moment from "moment";
import { NsDynamicForm } from "mis-crystal-design-system/dynamic-form/dynamic-form.namespace";
import { ParkingDisclaimerComponent } from "../../modals/parking-disclaimer/parking-disclaimer.component";

const parkingTypes = {
  PARKING_TWO: "TWO-WHEELER-PARKING",
  PARKING_FOUR: "FOUR-WHEELER-PARKING"
};

const seatAddon: NsEmpWebExpData.IAddon = {
  id: "SEAT",
  name: "Add Desk",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.DESK_BULE,
  iconBgColor: "#F2FFEF",
  mandatory: false
};

const commuteAddon: NsEmpWebExpData.IAddon = {
  id: "COMMUTE",
  name: "Add Cab/Parking",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.CAB,
  iconBgColor: "#FDF5FD",
  mandatory: false
};

const mealAddon: NsEmpWebExpData.IAddon = {
  id: "MEAL",
  name: "Add Meals",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.MEAL,
  iconBgColor: "#F2FFEF",
  mandatory: false
};

const otherDetails: NsEmpWebExpData.IAddon = {
  id: "OTHER_DETAILS",
  name: "Other Details",
  icon: CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.LIST,
  iconBgColor: "#FFF3E1",
  mandatory: false
};

@Component({
  selector: "mis-employee-web-exp-booking-form",
  templateUrl: "./booking-form.component.html",
  styleUrls: ["./booking-form.component.scss"]
})
export class BookingFormComponent implements OnInit, OnDestroy {
  Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  loginShiftList: NsEmpWebExpData.IShiftData[] = [];
  logoutShiftList: NsEmpWebExpData.IShiftData[] = [];
  nextDaylogoutShiftList: NsEmpWebExpData.IShiftData[] = [];
  offices: NsEmpWebExpData.IOffice[] = [];
  preferenceData: NsEmpWebExpData.IPreference;
  mealMapValue: NsEmpWebExpData.IMealsEntity[] = [];
  meal: NsEmpWebExpData.IMeal[] = [];
  private endObs = new Subject<void>();
  bookingForm: FormGroup;
  prefOfc: NsEmpWebExpData.IOffice;
  dateFormat: string = "DD/MM/YYYY";
  startDatepickerConfig: Partial<IDatePickerConfig> = {
    minDate: parseZone().tz(this.bookingSvc?.timezone)?.format(this.dateFormat),
    format: this.dateFormat,
    maxDate: parseZone().tz(this.bookingSvc?.timezone)?.format(this.dateFormat),
    timezone: this.bookingSvc?.timezone
  };
  endDatepickerConfig: Partial<IDatePickerConfig> = {
    minDate: parseZone().tz(this.bookingSvc?.timezone)?.format(this.dateFormat),
    format: this.dateFormat,
    maxDate: parseZone().tz(this.bookingSvc?.timezone)?.format(this.dateFormat),
    timezone: this.bookingSvc?.timezone
  };
  formReset = new Subject<void>();
  formHeading = "Create Booking";
  readonly other = "OTHERS";
  loader = false;
  parkingOptions: NsEmpWebExpData.IPremise[] = [];
  availableAddons: NsEmpWebExpData.IAddon[] = [];
  addonConfigurations: NsEmpWebExpData.IAddon;
  addonDataSubscription: Subscription;
  maxDuration: number = 0;
  recurrenceMaxDuration: number = 30;
  recurrenceStartDate;
  bookingReasons = [] || "";
  disableOfficeChange = false;
  @Input() formConfigs: NsEmpWebExpData.IBookingFormConfig;
  @Output() create = new EventEmitter<[NsEmpWebExpData.IBookingRequest[] | NsEmpWebExpData.IBookingRequestObject, boolean]>(true);
  @Output() update = new EventEmitter<NsEmpWebExpData.IBookingRequest[]>(true);
  @Output() close = new EventEmitter<void>(true);
  @Output() expand = new EventEmitter<{ addon: NsEmpWebExpData.IAddon; shiftValid: boolean }>(true);
  configurations: NsWISConfigurations.IConfigurations;
  drawerExpanded = false;
  datesDisabled: string[] = [];
  weeklyOffs: number[] = [];
  datepickerMessages: IDatePickerToastText[] = [];
  enableWeeklyOffBookings: boolean = false;
  isWeeklyOff: boolean = false;
  adhocEnabled = false;
  checkoutList = [];
  startTimeInMinutes: number;
  recurrenceBookingEnabled = false;
  singleShiftEnabled = false;
  mealCutOff: boolean;
  officeFormControl = new FormControl({}, [Validators.required]);
  mealStartCutOff: number;
  mealEndCutOff: number;
  mealCutOffPassed: boolean;
  parkingLoading: boolean;
  showOfficeInfo: boolean;
  parkingScheduleCutoff: number;
  transportScheduleCutoff: string;
  parkingDisabled: boolean = false;
  transportDisabled: boolean = false;
  startDate: any;
  maxDate: any;
  formattedMaxDate: any;
  shiftType: string = "LOGIN";
  loginShiftId: string = null;
  allocatedSeatId: string = "";
  //Dynamic Fields implemntation
  dynamicShiftTimes: any;
  enableDynamicFields: boolean = false;
  dynamicFields: Array<NsDynamicForm.IDynamicField>;
  formValues: { [key: string]: any } = {};
  isDynamicFormValid: boolean = true;
  outputValue: any;
  disableForm: boolean = false;

  projectCodeEnabledOnBookingMandatory: boolean = false;
  projectCodeEnabledOnBooking: boolean = false;
  projectCode: string = '';
  public projectCodeSearchURL!: (searchQuery: string) => Observable<Array<string>>;
  //parking disclaimer
  parkingDisclaimerAck = false;
  bookingModalEnabled = false;
  disableHolidayDates: string[] = [];

  isParkingSignedIn = false;

  constructor(
    public bookingSvc: BookingDataService,
    private preferenceSvc: PreferenceDataService,
    private apiService: ApiService,
    private toasts: ToastService,
    public configSvc: ConfigurationService,
    private commuteSvc: CommuteService,
    private seatSvc: SeatService,
    private mealService: MealService,
    private modals: ModalService,
    private translateSvc: TranslateService,
    private changeDetectionRef: ChangeDetectorRef,
    private userService: EmpUserService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit(): void {
    const url = "booking-form/api.json";
    this.parkingScheduleCutoff = this.formConfigs.userConfigs.flattenedSettings.parkingScheduleCutoff;
    this.enableWeeklyOffBookings = this.formConfigs.userConfigs.flattenedSettings.enableWeeklyOffBookings;
    this.projectCodeSearchURL = this.apiService.getProjectCode();
    this.projectCodeEnabledOnBooking = this.formConfigs.userConfigs.flattenedSettings.projectCodeEnabledOnBooking;
    this.projectCodeEnabledOnBookingMandatory = this.formConfigs.userConfigs.flattenedSettings.projectCodeEnabledOnBookingMandatory;
    let startDate = !!this.formConfigs.nearbyBookingDetails?.startTime
      ? parseZone(this.formConfigs.nearbyBookingDetails?.startTime)?.valueOf().toString()
      : parseZone().valueOf().toString();
    combineLatest([
      this.formConfigs.mode !== "EDIT"
        ? this.apiService
            .getEmpPreferences({
              bookingType: this.formConfigs.type,
              startTime: startDate,
              officeGuid: this.formConfigs.profile.officeGuid
            })
            .pipe(
              catchError(err => {
                this.toasts.displayMsg("Unable to fetch preferences.", 4000);
                return throwError("Preference fetch error. Err:", err);
              })
            )
        : of({}),
      this.bookingSvc.offices.pipe(
        catchError(err => {
          this.toasts.displayMsg("Unable to fetch offices!", 4000);
          return throwError("Offices fetch error. Err:", err);
        })
      )
    ])
      .pipe(take(1))
      .subscribe(([preference, offices]) => {
        this.preferenceSvc.refreshReplayStreams({
          bookingType: this.formConfigs.type,
          startTime: parseZone().tz(this.bookingSvc.timezone).valueOf().toString(),
          officeGuid: this.formConfigs.profile.officeGuid
        });
        this.preferenceData = preference; // to check if meal has to be defaulted
        if (
          !this.formConfigs.userConfigs.flattenedSettings.enableEmployeePreferences &&
          !this.formConfigs.userConfigs.flattenedSettings.autoPopulateBookingForm
        ) {
          this.preferenceData.loginShiftId = null;
          this.preferenceData.logoutShiftId = null;
        }
        this.showOfficeInfo = this.formConfigs.userConfigs.flattenedSettings.showOfficeInfoOnBookingForm;
        if (this.showOfficeInfo) {
          this.offices = offices.map(o => ({
            ...o,
            label: { primaryText: o.address, secondaryText: o?.fullAddress },
            value: o.guid
          }));
        } else {
          this.offices = offices.map(o => ({
            ...o,
            label: o.address,
            value: o.guid
          }));
        }
        this.dateFormat = this.formConfigs.userConfigs.flattenedSettings.DATE_FORMAT;
        const ofc = this.offices.find(ofc => ofc.guid === (this.preferenceData.officeGuid || this.formConfigs.profile.officeGuid));
        let booking = this.formConfigs.data;
        if (this.formConfigs.mode === "CREATE" && this.formConfigs.nearbyBookingDetails) {
          booking = {
            ...booking,
            officeId: this.formConfigs.nearbyBookingDetails?.officeGuid,
            startTime: this.formConfigs.nearbyBookingDetails?.startTime,
            endTime: this.formConfigs.nearbyBookingDetails?.endTime
          };
        } else if (this.formConfigs.mode === "CREATE" && ofc) {
          this.officeFormControl.patchValue(ofc);
          booking = {
            ...booking,
            officeId: ofc.guid
          };
        }
        this.bookingForm = this.buildForm(booking);

        this.setFormFieldStates(this.bookingForm);
        this.setChangelisteners(this.bookingForm);
        this.checkAndGetShifts(this.bookingForm.get("startDate"), this.bookingForm.get("endDate"));
        this.attributeSetterForOffice(booking);
        this.parkingTransportCutoff();
      });
    this.seatSvc.officeChange.pipe(takeUntil(this.endObs)).subscribe(res => {
      const ofc = this.offices.find(o => o.guid === res?.officeGuid);
      this.onOfficeChange(ofc, true);
      this.bookingForm.patchValue({ seat: res });
    });

    this.commuteSvc.removeParking.subscribe({
      next: (res: boolean) => {
        if (!!res && res) {
          let opt: NsEmpWebExpData.IAddon = {
            icon: "assets/libraries/mis-employee-web-exp/icons/ic-ui-car-24.svg",
            iconBgColor: "#FDF5FD",
            id: "COMMUTE",
            mandatory: false,
            name: "Add Cab/Parking"
          };
          this.removeAddon(opt);
        }
      }
    });
  }
  parkingTransportCutoff() {
    if (this.parkingScheduleCutoff >= 0) {
      const today = parseZone().tz(this.bookingSvc?.timezone);
      this.maxDate = today.add(this.parkingScheduleCutoff, "days");
      this.formattedMaxDate = this.maxDate.format("MMM DD, YYYY");
    }
  }

  checkOnlyParkingEnabled() {
    return this.bookingSvc.formConfiguration.type !== "OFFICE"
      ? true
      : !(
          !!this.formConfigs?.userConfigs?.flattenedSettings?.onlyParkingBookingEnabled &&
          this.formConfigs?.userConfigs?.flattenedSettings?.onlyParkingBookingEnabled
        );
  }
  attributeSetterForOffice(booking?: NsEmpWebExpData.IBookingDetailDTOS) {
    this.availableAddons.length = 0;
    this.dateFormat = this.formConfigs.userConfigs.flattenedSettings.DATE_FORMAT;
    this.formHeading = this.formConfigs.heading;
    this.seatSvc.setSeatMapSource(this.formConfigs.userConfigs?.flattenedSettings);
    const configs = this.formConfigs.userConfigs as NsWISConfigurations.IConfigurations;
    const bookingFeatures = configs.BUSettings.features.find(f => f.id === "booking");
    seatAddon.mandatory = configs.flattenedSettings.seatMandatory;
    commuteAddon.mandatory = configs.flattenedSettings.parkingBookingMandatory;
    bookingFeatures?.seatBookingEnabled && this.availableAddons.push(seatAddon);
    commuteAddon.icon = configs.flattenedSettings.isShuttleRequired
      ? CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.SHUTTLE
      : CONSTANTS_ASSETS_EMP_WEB_EXP.ICONS.CAB;
    (configs.flattenedSettings.showCabs || configs.flattenedSettings.showParking) && this.availableAddons.push(commuteAddon);
    this.parkingLoading =
      (configs.flattenedSettings.showCabs || configs.flattenedSettings.showParking) &&
      (this.preferenceData.loginTransport || this.preferenceData.logoutTransport || !!this.preferenceData.parking?.premiseId) &&
      this.formConfigs.type === "OFFICE";
    const mealEnabled = configs.flattenedSettings.mealPlanningEnabled;
    mealAddon.mandatory = configs.flattenedSettings.mealPlanningMandatory;
    const adhocCheck = configs.BUSettings.features.find(item => item.id === "schedule");
    this.adhocEnabled = adhocCheck?.adhocEnabled;
    this.bookingModalEnabled =
      (
        configs.flattenedSettings?.bookingDisclaimers?.parking?.title &&
        configs.flattenedSettings?.bookingDisclaimers?.parking?.actionButtonName &&
        configs.flattenedSettings?.bookingDisclaimers?.parking?.message
      )?.length > 0;
    if (mealEnabled) {
      this.availableAddons.push(mealAddon);
      this.mealService.userBuid = this.userService.employeeInfo.buid;
      this.mealService.empGuid = this.userService.employeeInfo.empGuid;
    }
    this.formHeading = this.formConfigs.heading;
    this.recurrenceBookingEnabled = this.formConfigs.userConfigs.flattenedSettings.recurrenceBookingEnabled;
    this.singleShiftEnabled = this.formConfigs.userConfigs.flattenedSettings.singleShiftOperations;
    //Enable Dynamic Fields
    this.enableDynamicFields = configs.flattenedSettings.enableDynamicFields;
    otherDetails.mandatory = configs.flattenedSettings.isDynamicFieldsMandatory;
    if (this.enableDynamicFields) {
      this.availableAddons.push(otherDetails);
    }
    //
    if (this.formConfigs.type === "WFH" || configs.flattenedSettings.approvalFlowEnabled) {
      this.setBookingReasons(booking);
    }
    const date = parseZone()
      .tz(this.bookingSvc.timezone)
      .set("hours", 0)
      .set("minutes", 0)
      .set("seconds", 0)
      .set("milliseconds", 0)
      .format(this.dateFormat);
    this.startDatepickerConfig.format = this.dateFormat;
    this.endDatepickerConfig.format = this.dateFormat;
    this.startDatepickerConfig.minDate = date;
    this.endDatepickerConfig.minDate = date;
    this.apiService
      .getProfileRules(this.bookingForm.get("office").value.guid)
      .pipe(takeUntil(this.endObs))
      .subscribe(res => {
        this.maxDuration = res.bulkScheduleAllowedDays;
        this.recurrenceMaxDuration = Math.min(res.bulkScheduleAllowedDays, this.recurrenceMaxDuration);
        this.bookingForm.get("recurrence")?.get("repeatsEvery")?.get("repeatedDay")?.clearValidators();
        this.bookingForm
          .get("recurrence")
          ?.get("repeatsEvery")
          ?.get("repeatedDay")
          ?.setValidators([Validators.max(this.recurrenceMaxDuration), Validators.min(2)]);
        const maxMoment = parseZone().add(this.maxDuration, "days").tz(this.bookingSvc.timezone);
        this.startDatepickerConfig.maxDate = maxMoment.format(this.dateFormat);
        this.endDatepickerConfig.maxDate = maxMoment.format(this.dateFormat);
        this.setDatepickerDates(maxMoment);
        if (this.bookingSvc.wfoExpiryTime) {
          this.startDatepickerConfig.maxDate = this.bookingSvc.wfoExpiryTime.format(this.dateFormat);
          this.endDatepickerConfig.maxDate = this.startDatepickerConfig.maxDate;
        }
      });
  }

  handleRecurrenceData(data: FormGroup) {
    const { recurrenceVisibility, endDate, repeatsEvery } = data.value;
    if (repeatsEvery.days) {
      let newArray = [];
      newArray.push(
        repeatsEvery.days.map(item => {
          if (item === 7) item = 1;
          else item = item + 1;
          return item;
        })
      );
      repeatsEvery.days = newArray[0];
    }
    this.bookingForm.controls.recurrence.patchValue({ recurrenceVisibility, endDate, repeatsEvery });
    (this.bookingForm.controls.recurrence as FormGroup).controls = data.controls;
  }

  get inputText(): FormControl {
    return this.bookingForm.get("inputText") as FormControl;
  }

  openAdhoc(data: string) {
    if (data === "Check-Out") {
      this.shiftType = "LOGOUT";
      this.loginShiftId = this.bookingForm.get("startShift").value?.shiftId;
      this.loader = true;
      this.bookingSvc
        .getShifts(
          this.bookingForm.get("startDate"),
          this.bookingForm.get("endDate"),
          this.bookingForm.value.office,
          this.shiftType,
          this.loginShiftId
        )
        .subscribe(([login, logout, nextDay, premises]) => {
          this.loader = false;
          const initialState = {
            ignoreBackdropClick: true,
            class: "exp-adhoc-modal",
            configs: this.formConfigs.userConfigs,
            data: data,
            shifts: [this.loginShiftList, logout],
            nextDayLogoutShift: this.nextDaylogoutShiftList,
            formData: this.bookingForm,
            adhocEnabled: this.adhocEnabled
          };
          const ref = this.modals.show(AdhocShiftsComponent, { ...initialState, initialState });
          ref.afterClosed.subscribe((res: any) => {
            if (res) {
              this.onShiftChange(res.shiftType.toLowerCase(), res);
              const { startShift, endShift, office, startDate, meal } = this.bookingForm.value;
              let startTime = tz(startDate, this.dateFormat, office.timezone).startOf("day");
              if (this.formConfigs.mode === "EDIT" && meal) {
                this.getHoursAndMiutes(this.bookingForm);
                this.premiseWiseMealCutoff();
                const bkng = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
                startTime = parseZone(bkng.startTime)
                  .tz(bkng.timezone || office.timezone)
                  .startOf("day");
                const start = startShift ? startTime?.valueOf() + startShift?.timeInMinutes * 60 * 1000 : -1;
                const end = endShift ? startTime?.valueOf() - 1 + endShift?.timeInMinutes * 60 * 1000 : -1;
                this.seatSvc.seatChange.pipe(takeUntil(this.endObs)).subscribe(res => {
                  if (res?.floorId && start !== -1 && end !== -1)
                    this.mealService.startEndShiftData(start, end, office.timezone, res?.floorId, office.guid);
                  else this.mealService.startEndShiftData(start, end, office.timezone, null, office.guid);
                });
                this.mealService.changedGuid(office.value);
                if (meal) {
                  this.mealService.bookingResponse(meal);
                } else {
                  this.mealService.changedMealData(undefined);
                }
              }
            }
          });
        });
    } else {
      const initialState = {
        ignoreBackdropClick: true,
        class: "exp-adhoc-modal",
        configs: this.formConfigs.userConfigs,
        data: data,
        shifts: [this.loginShiftList, this.logoutShiftList],
        nextDayLogoutShift: this.nextDaylogoutShiftList,
        formData: this.bookingForm,
        adhocEnabled: this.adhocEnabled
      };
      const ref = this.modals.show(AdhocShiftsComponent, { ...initialState, initialState });
      ref.afterClosed.subscribe((res: any) => {
        if (res) {
          this.onShiftChange(res.shiftType.toLowerCase(), res);
          const { startShift, endShift, office, startDate, meal } = this.bookingForm.value;
          let startTime = tz(startDate, this.dateFormat, office.timezone).startOf("day");
          if (this.formConfigs.mode === "EDIT" && meal) {
            this.getHoursAndMiutes(this.bookingForm);
            this.premiseWiseMealCutoff();
            const bkng = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
            startTime = parseZone(bkng.startTime)
              .tz(bkng.timezone || office.timezone)
              .startOf("day");
            const start = startShift ? startTime?.valueOf() + startShift?.timeInMinutes * 60 * 1000 : -1;
            const end = endShift ? startTime?.valueOf() - 1 + endShift?.timeInMinutes * 60 * 1000 : -1;
            this.seatSvc.seatChange.pipe(takeUntil(this.endObs)).subscribe(res => {
              if (res?.floorId && start !== -1 && end !== -1)
                this.mealService.startEndShiftData(start, end, office.timezone, res?.floorId, office.guid);
              else this.mealService.startEndShiftData(start, end, office.timezone, null, office.guid);
            });
            this.mealService.changedGuid(office.value);
            if (meal) {
              this.mealService.bookingResponse(meal);
            } else {
              this.mealService.changedMealData(undefined);
            }
          }
        }
      });
    }
  }

  handleRecurrenceEvent() {
    !(this.bookingForm.get("recurrence") as FormGroup).get("endDate").value &&
      (this.bookingForm.get("recurrence") as FormGroup).patchValue({ endDate: this.recurrenceStartDate });
    this.bookingForm.get("recurrence").patchValue({ recurrenceVisibility: !this.bookingForm.get("recurrence").value.recurrenceVisibility });
    this.changeDetectionRef.detectChanges();
    this.analytics.send("emp_web_wfo_form_recurring", "emp_web", "click", this.bookingForm.get("recurrence").value.recurrenceVisibility);
  }

  private setDatepickerDates(maxMoment: Moment): void {
    const today = moment().tz(this.bookingSvc?.timezone).startOf("day");
    const r = parseZone().add(this.recurrenceMaxDuration, "days").tz(this.bookingSvc.timezone).startOf("day");
    let tempDate = parseZone().tz(this.bookingSvc?.timezone).startOf("day");
    this.apiService
      .getAllowedDates(today.format("YYYY-MM-DD"), maxMoment.format("YYYY-MM-DD"), [this.officeFormControl.value.guid])
      .subscribe((response: any) => {
        const dates = response.allowedDates.map(date => parseZone(date).tz(this.bookingSvc?.timezone));
        const endIndex = dates.findIndex(m => m.format("YYYY-MM-DD") === maxMoment.format("YYYY-MM-DD"));
        const pluckedMoments = dates.slice(0, endIndex + 1);
        pluckedMoments.sort((a, b) => b?.valueOf() - a?.valueOf());
        const lastValidDay = pluckedMoments.find(m => {
          const dayOfWeek = m.day();
          return !this.formConfigs.profile.empWeeklyOff.includes((dayOfWeek % 7) + 1);
        });
        this.recurrenceStartDate = lastValidDay && lastValidDay.format(this.formConfigs.userConfigs.flattenedSettings.DATE_FORMAT);
        this.disableHolidayDates = response.holidayDates;
        this.disableForm = false;
        tempDate = moment().tz(this.bookingSvc?.timezone).startOf("D");
        while (tempDate.isSameOrBefore(maxMoment)) {
          let date = moment();
          dates.forEach(date => {
            if (date.isSame(tempDate, "day")) date = date.clone();
          });
          const isEmpWeeklyOffIncludes = this.formConfigs?.profile?.empWeeklyOff.includes(tempDate.day() + 1);
          const disableHolidayDate = response.holidayDates.some(res => res === tempDate.format("YYYY-MM-DD"));
          if (isEmpWeeklyOffIncludes || (!date && date !== undefined) || disableHolidayDate) {
            const dateWithFormat = tempDate.format(this.dateFormat);
            this.datesDisabled.push(dateWithFormat);
            this.enableWeeklyOffBookings && this.datesDisabled ? (this.isWeeklyOff = true) : (this.isWeeklyOff = false);
            const message = isEmpWeeklyOffIncludes
              ? this.translateSvc.instant("Weekly Off")
              : disableHolidayDate
              ? this.translateSvc.instant("Booking not allowed as per organization policy")
              : this.translateSvc.instant("Not allowed due to company policies");
            this.datepickerMessages.push({ date: dateWithFormat, message });
          } else if (this.bookingSvc.wfoExpiryTime && tempDate.isAfter(this.bookingSvc.wfoExpiryTime.clone().startOf("day"))) {
            this.datepickerMessages.push({
              date: tempDate.format(this.dateFormat),
              message: this.translateSvc.instant("You are not allowed to book on this date. Please reach out to the admin for more details.")
            });
          }
          tempDate.add(1, "day");
        }
      });
  }

  ngOnDestroy(): void {
    this.endObs.next();
    this.endObs.complete();
    this.addonDataSubscription?.unsubscribe();
    this.formReset.next();
    this.formReset.complete();
    this.commuteSvc.clearFormData();
  }

  findDisableDates() {
    this.disableForm = false;
    this.disableHolidayDates.forEach(holidayDate => {
      const startDate = moment(this.bookingForm.get("startDate").value);
      if (moment(holidayDate).isSame(startDate, "day")) {
        this.disableForm = true;
        this.toasts.displayMsg(this.translateSvc.instant("Booking not allowed as per organization policy"), 4000);
      }
    });
  }

  closeForm() {
    this.close.emit();
    this.mealService.bookingResponse(null);
    this.seatSvc.changeSeat(undefined);
    this.drawerExpanded = false;
  }

  processMealDataForForm(data?) {
    const mealDataFromBooking = getAdditionalBookings(this.formConfigs.data, ["MEALS"])?.[0];
    const mealAdditionalBooking = mealDataFromBooking && JSON.parse(JSON.stringify(mealDataFromBooking));
    let mealMap = new Map();
    if (mealAdditionalBooking?.extras?.mealData)
      mealAdditionalBooking?.extras?.mealData.forEach(mealdata => {
        mealMap.set(mealdata.mealId, mealdata);
      });
    const mealForForm: NsEmpWebExpData.IMeal = {
      mealMap: mealMap,
      mealData: mealAdditionalBooking.extras?.mealData,
      premiseName: mealAdditionalBooking.extras?.premiseName,
      premiseId: mealAdditionalBooking.extras?.premiseId,
      premiseEndTime: mealAdditionalBooking.extras?.premiseEndTime,
      premiseStartTime: mealAdditionalBooking.extras?.premiseStartTime
    };
    return mealForForm;
  }

  processMealPreferences() {
    this.preferenceData.meals.mealData = this.applyCutOffsOnMeal(this.preferenceData?.meals?.mealData);
    let modifiedPreference = [];
    this.preferenceData?.meals?.mealData.map(item => {
      modifiedPreference = this.preferenceData?.meals?.mealData.filter(cutOff => cutOff.mealCutOff === true);
    });
    this.preferenceData.meals.mealData = modifiedPreference;
    let mealMap = new Map();
    this.preferenceData?.meals?.mealData.forEach(mealdata => {
      mealMap.set(mealdata.mealId, mealdata);
    });
    this.preferenceData.meals.mealMap = mealMap;
  }

  premiseWiseMealCutoff() {
    const premiseId = this.apiService
      .getMealCutoffPremiseWise(this.bookingForm.value.meal?.[0]?.premiseId)
      .pipe(takeUntil(this.endObs))
      .subscribe(item => {
        const mealArray = this.bookingForm.value.meal?.[0]?.mealData.map(meal => {
          return item.find(response => response.mealId === meal.mealId);
        });
        mealArray?.filter(item => item !== undefined);
        const appliedMealCutOffs = this.applyCutOffsOnMeal(mealArray);
        this.bookingForm.value.meal?.[0]?.mealData.forEach((mealItems, index) => {
          const findMeal = appliedMealCutOffs.find(id => id.mealId === mealItems?.mealId);
          if (findMeal) {
            mealItems.mealCutOff = findMeal.mealCutOff;
            mealItems.mealStartTime = findMeal.mealStartTime;
            mealItems.mealEndTime = findMeal.mealEndTime;

            this.bookingForm.value.meal[0].mealData[index] = mealItems;
          }
        });
        let mealMap = new Map();
        this.bookingForm.value.meal?.[0]?.mealData.forEach(mealdata => {
          mealMap.set(mealdata.mealId, mealdata);
        });
        if (this.bookingForm.value.meal?.[0]) this.bookingForm.value.meal[0].mealMap = mealMap;
      });
  }
  validateMealCutOff(mealItem) {
    return (
      (mealItem.mealStartTime >= this.mealStartCutOff && mealItem.mealStartTime <= this.mealEndCutOff) ||
      (mealItem.mealEndTime >= this.mealStartCutOff && mealItem.mealEndTime <= this.mealEndCutOff) ||
      (this.mealStartCutOff >= mealItem.mealStartTime && this.mealEndCutOff <= mealItem.mealEndTime)
    );
  }
  applyCutOffsOnMeal(mealWithCutOff) {
    if (mealWithCutOff) {
      return mealWithCutOff.map(mealItem => {
        mealItem.mealCutOff = this.validateMealCutOff(mealItem);
        return mealItem;
      });
    }
  }

  private buildForm(booking?: NsEmpWebExpData.IBookingDetailDTOS) {
    let office;
    let seat: NsEmpWebExpData.ISeatData;
    let meal: NsEmpWebExpData.IMeal[] = [];
    const ofc = this.offices.find(ofc => ofc.guid === (this.preferenceData?.officeGuid || this.formConfigs.profile?.officeGuid));
    if (this.formConfigs.mode === "CREATE") {
      if (this.preferenceData.seatDetails && this.formConfigs.type !== "WFH") {
        seat = this.preferenceData.seatDetails;
      }
    }
    let startDate: string;
    let endDate: string;
    const today = parseZone().tz(this.bookingSvc?.timezone);
    if (!this.formConfigs?.profile?.empWeeklyOff.includes(today.day() + 1) && !this.datesDisabled.find(d => parseZone(d).isSame(today, "day"))) {
      startDate = today.clone().format(this.dateFormat);
      endDate = today.clone().format(this.dateFormat);
    }
    if (booking) {
      this.projectCode = !!booking?.extras?.projectCode ? booking.extras.projectCode : '';
      const ofcFind = this.offices.find(ofcHere => ofcHere.guid === booking?.officeId);
      office = ofcFind || ofc;
      this.officeFormControl.patchValue(office);
      startDate = booking.startTime
        ? parseZone(booking.startTime)
            .tz(booking?.timezone || office?.timezone || this.bookingSvc?.timezone)
            .format(this.dateFormat)
        : startDate;

      endDate = booking.startTime
        ? parseZone(booking.startTime)
            .tz(booking?.timezone || office?.timezone || this.bookingSvc?.timezone)
            .format(this.dateFormat)
        : endDate;

      if (booking.tokenId && booking.tokenName && this.formConfigs.type === "OFFICE") {
        seat = {
          seatId: booking.tokenId,
          seatName: booking.tokenName,
          floorId: booking.premiseId,
          floorName: booking.premiseName
        };
      }
      meal = booking && getAdditionalBookings(this.formConfigs.data, ["MEALS"])?.[0] ? [this.processMealDataForForm()] : undefined;
    }
    if (seat?.seatId) {
      this.seatSvc.changeSeat(seat);
    }
    const todayDate = moment().tz(this.bookingSvc?.timezone).startOf("day");
    const disableHolidayDate = this.disableHolidayDates.some(res => res === todayDate.clone().format("YYYY-MM-DD"));
    if (disableHolidayDate) {
      startDate = "";
      endDate = "";
    }
    const configs = this.formConfigs.userConfigs as NsWISConfigurations.IConfigurations;
    const bookingFeatures = configs.BUSettings.features.find(f => f.id === "booking");
    const form = new FormGroup(
      {
        bookingRequestType: new FormControl(this.formConfigs.type || "OFFICE", [Validators.required]),
        office: new FormControl(office, [Validators.required]),
        startDate: new FormControl(startDate, this.formConfigs.mode === "CREATE" ? [Validators.required] : []),
        endDate: new FormControl(endDate, this.formConfigs.mode === "CREATE" ? [Validators.required] : []),
        startShift: new FormControl("", [Validators.required]),
        endShift: new FormControl("", [Validators.required]),
        premiseId: new FormControl("", [Validators.required]),
        projectCode: new FormControl(
          !!this.projectCode ? this.projectCode : '',
          this.formConfigs.type === 'OFFICE' && this.projectCodeEnabledOnBooking && this.projectCodeEnabledOnBookingMandatory
            ? [Validators.required]
            : []
        ),
        commute: new FormGroup(
          {
            login: new FormControl(undefined, []),
            logout: new FormControl(undefined, []),
            registrationNumber: new FormControl("", []),
            entityTypeId: new FormControl(
              !!booking?.additionalBookings && !!booking?.additionalBookings[0]?.extras.entityTypeId
                ? booking?.additionalBookings[0]?.extras.entityTypeId
                : "",
              []
            ),
            vehicleId: new FormControl(
              !!booking?.additionalBookings && !!booking?.additionalBookings[0]?.extras?.vehicleId
                ? booking?.additionalBookings[0]?.extras?.vehicleId
                : "",
              []
            )
          },
          this.formConfigs.userConfigs.flattenedSettings.parkingBookingMandatory &&
          this.formConfigs.userConfigs.flattenedSettings.showParking &&
          this.formConfigs.type === "OFFICE"
            ? [parkingValidator]
            : []
        ),
        seat: new FormControl(
          seat?.seatId ? seat : null,
          this.formConfigs.userConfigs.flattenedSettings.seatMandatory && bookingFeatures?.seatBookingEnabled && this.formConfigs.type === "OFFICE"
            ? [Validators.required]
            : []
        ),
        meal: new FormControl(
          meal?.length ? meal : undefined,
          this.formConfigs.userConfigs.flattenedSettings.mealPlanningMandatory &&
          configs?.flattenedSettings?.mealPlanningEnabled &&
          this.formConfigs.type === "OFFICE"
            ? [Validators.required]
            : []
        ),
        specialRequests: new FormControl(
          "",
          this.formConfigs.userConfigs.flattenedSettings.isDynamicFieldsMandatory &&
          configs?.flattenedSettings?.enableDynamicFields &&
          this.formConfigs.type === "OFFICE"
            ? [Validators.required]
            : []
        ),
        reason: new FormControl(booking?.bookingReason || "", this.formConfigs.type === "WFH" ? [Validators.required] : []),
        inputText: new FormControl({ value: "", disabled: true }, [Validators.maxLength(50)]),
        recurrence: new FormGroup(
          {
            recurrenceVisibility: new FormControl(false),
            endDate: new FormControl(null, []),
            repeatsEvery: new FormGroup({
              days: new FormControl([], []),
              repeatedDay: new FormControl(2, [Validators.min(2)])
            })
          },
          [recurrenceValidator]
        ),
        dynamicSpecialRequests: new FormControl()
      },
      [bookingReasonValidator]
    );
    return form;
  }

  getHoursAndMiutes(form: FormGroup, openeingFromPreference?: boolean) {
    if (form) {
      const { startShift, endShift, office } = form.getRawValue();
      this.mealStartCutOff =
        !openeingFromPreference && this.formConfigs.mode !== "EDIT"
          ? Math.floor(parseZone().tz(office.timezone).diff(parseZone(startShift).startOf("day")) / 60000)
          : Math.floor(parseZone(startShift).tz(office.timezone).diff(parseZone(startShift).startOf("day")) / 60000);
      this.mealEndCutOff =
        !openeingFromPreference && this.formConfigs.mode !== "EDIT"
          ? Math.floor(parseZone().tz(office.timezone).endOf("day")?.valueOf() / 60000)
          : Math.floor(parseZone(endShift).tz(office.timezone).diff(parseZone(endShift).startOf("day")) / 60000);
    }
  }

  getControl(name: string): AbstractControl {
    return this.bookingForm.get(name);
  }

  private setChangelisteners(form: FormGroup): void {
    this.formReset.next();
    const startDateCtrl = form.get("startDate");
    const endDateCtrl = form.get("endDate");
    const startShiftCtrl = form.get("startShift");
    const endShiftCtrl = form.get("endShift");
    const recurrenceDateCtrl = (form.get("recurrence") as FormGroup).get("endDate");
    const office = form.get("office");
    office.valueChanges.pipe(debounceTime(10), takeUntil(this.formReset)).subscribe(office => {
      this.seatSvc.changeSeat(undefined);
      this.checkAndGetShifts(startDateCtrl, endDateCtrl, true, false, false);
    });
    startDateCtrl.valueChanges.pipe(debounceTime(10), takeUntil(this.formReset)).subscribe((start: string) => {
      this.parkingTransportCutoff();
      this.bookingForm.get("recurrence").value.recurrenceVisibility = false;
      this.bookingForm.patchValue({ startShift: undefined, endShift: undefined, meal: undefined });
      this.endDatepickerConfig.minDate = start;
      endDateCtrl.patchValue(start, { emitEvent: false, onlySelf: false });
      if (!this.bookingSvc.formConfiguration.userConfigs.flattenedSettings.restrictBookingSingleDay)
        endDateCtrl.enable({ emitEvent: false, onlySelf: false });
      this.preferenceSvc.refreshReplayStreams({
        startTime: parseZone(start, this.dateFormat).tz(this.bookingSvc.timezone).startOf("day")?.valueOf().toString(),
        officeGuid: office.value.guid,
        bookingType: this.formConfigs.type
      });
      this.checkAndGetShifts(startDateCtrl, endDateCtrl, false, true);
    });
    endDateCtrl.valueChanges.pipe(debounceTime(10), takeUntil(this.formReset)).subscribe(end => {
      const recurrenceMaxEndDate = parseZone()
        .add(this.recurrenceMaxDuration, "days")
        .tz(this.bookingSvc.timezone)
        .startOf("day")
        .format(this.formConfigs.userConfigs.flattenedSettings.DATE_FORMAT)
        ?.valueOf();
      if (parseZone(end).isSameOrBefore(parseZone(recurrenceMaxEndDate))) {
        this.bookingForm.get("recurrence").patchValue({ endDate: end });
      } else {
        this.bookingForm.get("recurrence").patchValue({ endDate: recurrenceMaxEndDate });
      }
      this.bookingForm.patchValue({ startShift: undefined, endShift: undefined, meal: undefined });
      this.checkAndGetShifts(startDateCtrl, endDateCtrl);
    });
    recurrenceDateCtrl.valueChanges.pipe(skip(1)).subscribe(recurrenceEndDate => {
      endDateCtrl.patchValue(recurrenceEndDate, { emitEvent: false, onlySelf: false });
      if (!this.bookingSvc.formConfiguration.userConfigs.flattenedSettings.restrictBookingSingleDay)
        endDateCtrl.enable({ emitEvent: false, onlySelf: false });
    });
    startShiftCtrl.valueChanges.pipe(takeUntil(this.formReset)).subscribe(shift => endShiftCtrl.enable());
    combineLatest([startShiftCtrl.valueChanges, endShiftCtrl.valueChanges])
      .pipe(debounceTime(10), takeUntil(this.formReset))
      .subscribe(([startShift, endShift]) => {
        if (this.formConfigs.mode === "CREATE" && !this.preferenceData?.meals) {
          this.bookingForm.patchValue({ meal: undefined });
          this.bookingForm.patchValue({ specialRequests: undefined });
          this.mealService.changedMealData([]);
        }
        if (this.addonConfigurations) {
          this.updateAddon(this.addonConfigurations);
        }
      });
  }

  private setFormFieldStates(form: FormGroup): void {
    const startDate = form.get("startDate");
    const endDate = form.get("endDate");
    const startShift = form.get("startShift");
    const endShift = form.get("endShift");
    const { restrictBookingSingleDay } = this.formConfigs.userConfigs.flattenedSettings;
    // if(restrictBookingSingleDay) {
    //   endDate.disable();
    // }
    if (this.formConfigs.mode === "CREATE") {
      startDate.valid && !restrictBookingSingleDay ? endDate.enable() : endDate.disable();
      startDate.valid && (endDate.valid || endDate.disabled) ? startShift.enable() : startShift.disable();
      startDate.valid && (endDate.valid || endDate.disabled) && startShift.valid ? endShift.enable() : endShift.disable();
    } else {
      startDate.disable();
      endDate.disable();
      const { data: booking, userConfigs: configs } = this.formConfigs;
      const bookingStart = parseZone(booking.startTime);
      const bookingEnd = parseZone(booking.endTime);
      const now = parseZone();
      if (
        bookingStart.subtract(Number(configs.flattenedSettings.employeeScheduleCutoff), "hours").isAfter(now) &&
        booking.requestStatus !== "SIGNED_IN"
      ) {
        startShift.enable();
      } else {
        startShift.disable();
        startShift.clearValidators();
        startShift.updateValueAndValidity();
        this.startTimeInMinutes = parseZone(booking.startTime).diff(parseZone(booking.startTime).startOf("day")) / (1000 * 60);
      }

      if (
        bookingEnd.subtract(Number(configs.flattenedSettings.employeeEndTimeBookingCutoff), "hours").isAfter(now) &&
        booking.requestStatus !== "SIGNED_OUT"
      ) {
        endShift.enable();
      } else {
        endShift.disable();
        endShift.clearValidators();
        endShift.updateValueAndValidity();
      }
    }
    const cfg = this.formConfigs.userConfigs as NsWISConfigurations.IConfigurations;
    const schCfg = cfg.BUSettings.features.find(f => f.id === "schedule");
    this.disableOfficeChange = schCfg.showEmployeeProfileOfficeOnly || this.formConfigs?.data?.requestStatus === "SIGNED_IN";
  }

  onOfficeChange(office: NsEmpWebExpData.IOffice, external = false): void {
    this.bookingForm.patchValue({ office: office, meal: [], seat: undefined });
    this.bookingForm.get("commute").patchValue({ login: undefined, logout: undefined, registrationNumber: undefined });
    this.seatSvc.mapChangedOffice = external;
    this.officeFormControl.patchValue(office);
    this.apiService.getConfigurations(office.guid).subscribe(configs => {
      this.formConfigs.userConfigs = configs;
      const wfoAllowed = configs?.BUSettings?.features.find(x => x.id === "booking");
      if (!wfoAllowed.createBookingEnabled) {
        const messageToDisplay = this.showOfficeInfo ? (office.label as NsEmpWebExpData.IFullOfficeAddress).primaryText : office.label;
        this.toasts.displayMsg("Work from office is disabled for " + messageToDisplay);
      }
      this.attributeSetterForOffice();
      if (!this.singleShiftEnabled) {
        this.bookingForm.patchValue({ startShift: undefined, endShift: undefined });
      } else {
        this.setShifts();
      }
    });
    this.bookingForm.patchValue({ office, seat: undefined }, { emitEvent: true, onlySelf: true });
    this.seatSvc.changeSeat(undefined);
    if (this.bookingForm.get("startDate").value && this.bookingForm.get("endDate").value && this.formConfigs.mode === "CREATE")
      this.setdatepickerConfigs(office);
    // if (this.availableAddons.includes(seatAddon) && this.formConfigs.mode === "CREATE") {
    //   this.loader = true;
    //   const currentDate = new Date();
    //   const firstday = new Date(currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 1)).toUTCString();
    //   this.apiService.getUpdatedSeatPreference(new Date(firstday).getTime(), office.value).subscribe((seat) => {
    //     this.bookingForm.patchValue({ seat });
    //   });
    // }
  }

  expandAddon(addon: NsEmpWebExpData.IAddon): void {
    if (this.mealCutOff && addon.id === "MEAL") {
      this.showCutOffMsg();
    } else if ((addon.id === "SEAT" && this.disableOfficeChange && this.formConfigs?.data?.requestStatus === "SIGNED_IN") || (addon.id === 'COMMUTE' && this.isParkingSignedIn)) {
      return;
    } else if (
      addon.id === "COMMUTE" &&
      this.formConfigs.userConfigs.flattenedSettings.showParking &&
      !this.parkingDisclaimerAck &&
      this.bookingModalEnabled
    ) {
      this.showParkingDisclaimer(addon, true);
    } else {
      this.addonConfigurations = addon;
      this.updateAddon(addon);
      this.expand.emit({
        addon,
        shiftValid: this.bookingForm.get("startShift").valid && this.bookingForm.get("endShift").valid
      });
      this.drawerExpanded = true;
    }
  }

  async updateAddon(addon: NsEmpWebExpData.IAddon) {
    this.addonConfigurations = addon;
    this.addonDataSubscription?.unsubscribe();
    const { startShift, endShift, startDate, endDate, office, seat, meal } = this.bookingForm.getRawValue() || {};
    const ofc = this.offices.find(o => o.guid === office.value);
    let startTime = tz(startDate, this.dateFormat, ofc?.timezone).startOf("day");
    let endTime = tz(endDate, this.dateFormat, ofc?.timezone).startOf("day");
    if (this.formConfigs.mode === "EDIT" || this.formConfigs.mode === "CONVERT") {
      const bkng = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
      startTime = parseZone(bkng.startTime)
        .tz(bkng?.timezone || ofc?.timezone)
        .startOf("day");
      endTime = parseZone(bkng.endTime)
        .tz(bkng?.timezone || ofc?.timezone)
        .startOf("day");
    }
    if (addon.id === "COMMUTE") {
      this.loader = true;
      const commuteValue = this.bookingForm.get("commute")?.value;
      const login =
        commuteValue.login?.id === "parkingWaitlist"
          ? { ...commuteValue.login, value: commuteValue.login?.value.map(premise => premise.premiseId) }
          : commuteValue.login;
      await this.commuteSvc.setFormData(
        this.formConfigs.profile,
        this.formConfigs.userConfigs,
        this.bookingForm.getRawValue(),
        startTime?.valueOf() + this.bookingForm?.getRawValue()?.startShift?.timeInMinutes * 60 * 1000,
        this.bookingForm.value?.endShift?.isNextDay
          ? tz(endDate, this.dateFormat, ofc.timezone).startOf("day")?.valueOf() + this.bookingForm.value?.endShift?.timeInMinutes * 60 * 1000
          : startTime?.valueOf() + this.bookingForm.value?.endShift?.timeInMinutes * 60 * 1000,
        ofc.timezone,
        { ...this.bookingForm.get("commute")?.value, login },
        this.formConfigs.data,
        this.formConfigs.mode === "CREATE" ? startTime?.isBefore(endTime, "day") && endShift?.timeInMinutes > 1440 : false
      );
      this.loader = false;
      this.addonDataSubscription = this.commuteSvc.commuteChange.pipe(takeUntil(this.endObs)).subscribe(res => {
        if (this.formConfigs.mode === "EDIT" && this.bookingForm?.getRawValue()?.endShift && this.bookingForm?.getRawValue()?.startShift) this.handleCommuteChange(res);
        else {
          if (this.bookingForm.value?.endShift && this.bookingForm.value?.startShift) this.handleCommuteChange(res);
        }
      });
      this.analytics.send("emp_web_wfo_add_parking", "emp_web", "click", "");
    } else if (addon.id === "SEAT") {
      const start = startShift ? startTime?.valueOf() + startShift?.timeInMinutes * 60 * 1000 : -1;
      const end = endShift ? startTime?.valueOf() + endShift?.timeInMinutes * 60 * 1000 : -1;
      this.seatSvc.openMap(office, start, end, seat, this.formConfigs.nearbyBookingDetails);
      this.addonDataSubscription = this.seatSvc.seatChange.subscribe(res => {
        if (res?.seatId) {
          this.bookingForm.patchValue({ meal: undefined, seat: res });
        } else {
          this.bookingForm.patchValue({ seat: undefined });
        }
      });
      this.analytics.send("emp_web_wfo_add_desk", "emp_web", "click", "");
    } else if (addon.id === "MEAL") {
      let start;
      let end = endShift ? startTime?.valueOf() - 1 + endShift?.timeInMinutes * 60 * 1000 : -1;
      if (this.formConfigs.mode === "EDIT" && parseZone(startTime).tz(ofc?.timezone).date() === parseZone().tz(ofc?.timezone).date()) {
        start = parseZone().tz(ofc?.timezone)?.valueOf();
      } else {
        start = startShift ? startTime?.valueOf() + startShift?.timeInMinutes * 60 * 1000 : -1;
      }
      this.seatSvc.seatChange.subscribe(res => {
        if (res?.floorId && start !== -1 && end !== -1) this.mealService.startEndShiftData(start, end, ofc?.timezone, res?.floorId, ofc.guid);
        else this.mealService.startEndShiftData(start, end, ofc?.timezone, null, ofc.guid);
        this.mealService.changedGuid(office.value);
        if (meal) {
          this.mealService.bookingResponse(meal);
        } else {
          this.mealService.changedMealData(undefined);
        }
      });
      this.addonDataSubscription = this.mealService.mealChange.subscribe(res => {
        this.handleMealChange(res);
      });
    } else if ((addon.id = "OTHER_DETAILS")) {
      this.dynamicShiftTimes = this.bookingForm.get("startShift").value && this.bookingForm.get("endShift").value;
      if (this.dynamicShiftTimes) {
        this.loader = true;
        this.apiService.getDynamicFormMetaData().subscribe(
          res => {
            this.loader = false;
            this.dynamicFields = res.dynamicData;
            this.formValues = {};
            if (this.formConfigs?.data?.extras?.dynamicData) {
              this.modifyFormValuesRes();
            }
          },
          err => {
            this.loader = false;
            this.toasts.displayMsg(err?.error?.message);
          }
        );
      } else {
        this.bookingForm.patchValue({ dynamicSpecialRequests: undefined });
        this.formValues = {};
      }
    }
  }
  modifyFormValuesRes() {
    this.outputValue = JSON.parse(JSON.stringify(this.formConfigs?.data?.extras?.dynamicData));
    let output = this.outputValue;
    this.dynamicFields.map(x => {
      if (x.subFields) {
        for (const key in output) {
          if (key == x.configName) {
            if (output.hasOwnProperty(key)) {
              output[x.configName] = { value: output[key].length ? output[key][0] : output[key] };
            }
          }
          if (x.subFields) {
            x.subFields.map(y => {
              if (y.configName === key) {
                if (output[x.configName]) output[x.configName]["subFields"] = { [key]: output[key] };
              }
            });
          }
        }
      }
    });
    this.formValues = output && this.bookingForm.value.specialRequests ? output : {};
  }
  async checkAndGetShifts(
    start?: AbstractControl,
    end?: AbstractControl,
    officeChange?: boolean,
    updatePreferences: boolean = false,
    updateShifts: boolean = true
  ) {
    start = start || this.bookingForm.get("startDate");
    end = end || this.bookingForm.get("endDate");
    this.setMealCutoffInMinutes(start, end);
    if (start.invalid) {
      this.bookingForm?.get("startShift").disable();
      this.bookingForm?.get("endShift").disable();
    }
    end.invalid && this.bookingForm?.get("endShift").disable();
    if ((start.valid && (end.valid || end.disabled)) || this.formConfigs.mode === "EDIT" || this.formConfigs.mode === "CONVERT") {
      this.loader = true;
      if (!this.bookingForm.get("startDate").disabled && updatePreferences) {
        this.preferenceData = await this.preferenceSvc.otherDayPreference.pipe(take(1)).toPromise();
        this.parkingLoading = !!(this.preferenceData?.parking?.premiseId && this.formConfigs.type === "OFFICE");
      }
      this.bookingSvc
        .getShifts(start, end, this.bookingForm.value.office, this.shiftType, this.loginShiftId)
        .subscribe(([login, logout, nextDay, premises]) => {
          [this.loginShiftList, this.logoutShiftList, this.nextDaylogoutShiftList] = [login, logout, nextDay];
          if (this.nextDaylogoutShiftList.length > 0) {
            this.translateSvc.get("Next Day").subscribe(response => {
              if (response && this.logoutShiftList && this.nextDaylogoutShiftList) {
                const nextDayShifts = [{ label: response, value: response, type: "LEGEND", disabled: true }, ...this.nextDaylogoutShiftList];

                this.checkoutList = [...this.logoutShiftList, ...nextDayShifts];
              }
            });
          } else {
            this.checkoutList = [...this.logoutShiftList];
          }
          // this.bookingForm?.get("startShift").enable();
          // this.bookingForm?.get("endShift").enable();
          this.setParkingAndPremises(premises as NsEmpWebExpData.IPremise[]);
          officeChange
            ? (this.parkingLoading = false && this.bookingForm.patchValue({ startShift: undefined, endShift: undefined }))
            : updateShifts && this.setShifts();
          !officeChange &&
            this.setCommute(
              (this.preferenceData?.loginTransport || this.preferenceData?.logoutTransport) &&
                this.bookingForm.get("startShift")?.value &&
                this.bookingForm.get("endShift")?.value,
              true
            );
          this.loader = false;
          const seatAddon = this.availableAddons.find(addon => addon.id === "SEAT");
          if (
            this.formConfigs.mode === "CREATE" &&
            !!this.formConfigs.nearbyBookingDetails?.teamMateEmpGuid &&
            seatAddon &&
            (!this.addonConfigurations || this.addonConfigurations?.id === "SEAT")
          ) {
            this.expandAddon(seatAddon);
          }
          this.findDisableDates();
        });
    }
  }

  setProjetcCodeData(projectCode: string){
    setTimeout(()=>{
      this.projectCode = projectCode;
      this.bookingForm.patchValue({projectCode : projectCode});
    }, 10);
  }

  selectReasons(reason: string): void {
    this.bookingForm.patchValue({ reason: reason });
    if (reason === this.other) {
      this.bookingForm.get("inputText")?.enable();
      this.analytics.send("emp_web_wfh_other_reasons", "emp_web", "click", reason);
    } else {
      this.bookingForm.get("inputText")?.disable();
      this.analytics.send("emp_web_wfh_preset_reasons", "emp_web", "click", reason);
    }
  }
  setdatepickerConfigs(office: NsEmpWebExpData.IOffice): void {
    const today = parseZone().tz(office.timezone);
    const minDate = today.format(this.dateFormat);
    const maxDate = today.clone().add(this.maxDuration, "days");
    this.bookingSvc.timezone = office.timezone;
    this.startDatepickerConfig = {
      ...this.startDatepickerConfig,
      minDate,
      maxDate:
        this.bookingSvc.wfoExpiryTime && maxDate.isAfter(this.bookingSvc.wfoExpiryTime)
          ? this.bookingSvc.wfoExpiryTime.format(this.dateFormat)
          : maxDate.format(this.dateFormat),
      timezone: office.timezone
    };
    this.endDatepickerConfig = {
      ...this.endDatepickerConfig,
      minDate,
      maxDate:
        this.bookingSvc.wfoExpiryTime && maxDate.isAfter(this.bookingSvc.wfoExpiryTime)
          ? this.bookingSvc.wfoExpiryTime.format(this.dateFormat)
          : maxDate.format(this.dateFormat),
      timezone: office.timezone
    };
    this.bookingForm.patchValue({ startDate: minDate, endDate: minDate }, { emitEvent: false, onlySelf: false });
  }

  setMealCutoffInMinutes(start, end) {
    const timeZone = this.bookingForm.value?.office?.timezone;
    const mealCutoffInMinutes = this.formConfigs.userConfigs.flattenedSettings["mealCutoffInMinutes"];
    const setCutOff = -mealCutoffInMinutes;
    const selectedDate = tz(start.value, this.formConfigs.userConfigs.flattenedSettings.DATE_FORMAT, timeZone).startOf("day")?.valueOf();
    const todaysDate = parseZone().tz(timeZone)?.valueOf();
    const enableTillTime = selectedDate + setCutOff * 1000 * 60;
    this.mealCutOff = !(todaysDate < enableTillTime);
  }
  showCutOffMsg() {
    this.toasts.displayMsg("The latest time upto which you can book meals is already passed.");
  }

  private setParkingAndPremises(premises: NsEmpWebExpData.IPremise[]): void {
    if (!premises?.length) {
      this.toasts.displayMsg("No premises found for the office!");
      return;
    }
    const premise = premises.find(premise => premise.premiseTypeText === "BUILDING")?.premiseId;
    if (premise) {
      this.bookingForm.patchValue({ premiseId: premise });
    }
  }
  private singleShiftSetter(): void {
    const startShift = this.loginShiftList[0];
    const endShift = this.logoutShiftList[this.logoutShiftList.length - 1];
    this.bookingForm.patchValue({ startShift, endShift }, { onlySelf: true, emitEvent: false });
  }

  private setShifts(): void {
    this.bookingForm.patchValue({
      specialRequests:
        this.formConfigs?.data?.extras?.dynamicData && !this.dynamicFields && this.formConfigs.mode === "EDIT"
          ? this.formConfigs?.data?.extras?.dynamicData
          : this.formConfigs.data?.specialRequests
    });
    this.bookingForm.patchValue({ dynamicSpecialRequests: this.formConfigs.data?.specialRequests });
    if (this.formConfigs.mode === "EDIT" || this.formConfigs.mode === "CONVERT") {
      const booking = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
      const format = this.formConfigs.userConfigs.flattenedSettings.TIME_FORMAT;
      let startShift: { label: string; shiftNotFound: boolean } | NsEmpWebExpData.IShiftData = this.loginShiftList.find(
        s => s.shiftId === booking.loginShiftId || s.shiftId === this.preferenceData?.loginShiftId
      );
      if (!startShift) {
        startShift = labelForShifts(booking.startTime, booking.timezone, format);
        this.bookingForm.get("startShift").disable();
      }
      let endShift: { label: string; shiftNotFound: boolean } | NsEmpWebExpData.IShiftData = booking?.extras?.isNextDayLogout
        ? this.nextDaylogoutShiftList.find(s => s.shiftId === booking.logoutShiftId || s.shiftId === this.preferenceData?.logoutShiftId)
        : this.logoutShiftList.find(s => s.shiftId === booking.logoutShiftId || s.shiftId === this.preferenceData?.logoutShiftId);
      if (!endShift) {
        endShift = labelForShifts(booking.endTime, booking.timezone, format);
        this.bookingForm.get("endShift").disable();
      }
      this.bookingForm.patchValue({ startShift, endShift }, { onlySelf: true, emitEvent: false });
      this.formConfigs.data.requestStatus === "SIGNED_IN" && this.bookingForm.get("startShift").disable();
      this.parkingLoading = false;
    } else {
      const startShift = this.loginShiftList.find(
        s =>
          s.shiftId ===
          (this.formConfigs.nearbyBookingDetails?.loginShiftId ||
            this.preferenceData?.defaultLoginShift?.scheduleEventId ||
            this.preferenceData?.loginShiftId)
      );
      const endShift = [...this.logoutShiftList, ...this.nextDaylogoutShiftList].find(
        s =>
          s.shiftId ===
          (this.formConfigs.nearbyBookingDetails?.logoutShiftId ||
            this.preferenceData?.defaultLogoutShift?.scheduleEventId ||
            this.preferenceData?.logoutShiftId)
      );
      this.bookingForm.get("startShift").enable();
      this.bookingForm.get("endShift").enable();
      if (this.singleShiftEnabled && !(startShift && endShift)) {
        this.singleShiftSetter();
      } else if (startShift && endShift && startShift.timeInMinutes < endShift.timeInMinutes) {
        this.parkingLoading = !!(
          this.preferenceData?.parking?.premiseId ||
          this.preferenceData?.loginTransport ||
          (this.preferenceData?.logoutTransport && this.formConfigs.type === "OFFICE")
        );
        this.bookingForm.patchValue({ startShift, endShift }, { onlySelf: true, emitEvent: false });
        this.updateSeat();
        this.formConfigs.userConfigs.flattenedSettings.showParking && this.preferenceData.parking && this.updateParking();
        this.formConfigs.userConfigs.flattenedSettings.mealPlanningEnabled && this.preferenceData.meals && this.updateMeal();

        this.parkingLoading = false;
      } else {
        this.parkingLoading = false;
        this.bookingForm.patchValue({ seat: undefined });
      }
    }
    this.getHoursAndMiutes(this.bookingForm);
    this.formConfigs.mode === "EDIT" && this.bookingForm.get("meal").value && this.premiseWiseMealCutoff();
  }
  private setCommute(checkingPreference?: boolean, selection?: boolean): void {
    const booking = this.formConfigs.data as NsEmpWebExpData.IBookingDetailDTOS;
    let loginCommute;
    let logoutCommute;
    let regNo;
    if (this.formConfigs.mode === "CREATE" && checkingPreference) {
      loginCommute = this.preferenceData.loginTransport
        ? {
            id: "transport",
            disable: false,
            name: "Office Cab"
          }
        : this.preferenceData.parking?.premiseId
        ? {
            id: "parking",
            disable: false,
            value: {
              parentPremiseId: this.preferenceData?.parking?.premiseId,
              premiseId: this.preferenceData?.parking?.premiseId,
              premise: this.preferenceData?.parking?.premiseName,
              seatId: selection ? this.preferenceData?.parking?.tokenId : null,
              seatName: this.preferenceData?.parking?.tokenName,
              premiseType: this.preferenceData?.parking?.parkingType
            }
          }
        : undefined;

      logoutCommute = this.preferenceData.logoutTransport
        ? {
            id: "transport",
            disable: false,
            name: "Office Cab"
          }
        : undefined;
    } else {
      if (booking?.extras?.login || booking?.extras?.logout) {
        loginCommute = booking?.extras?.login
          ? {
              id: "transport",
              disable: false,
              name: "Office Cab"
            }
          : undefined;
        logoutCommute = booking?.extras?.logout
          ? {
              id: "transport",
              disable: false,
              name: "Office Cab"
            }
          : undefined;
      } else if (booking?.bookingRequestType === "OFFICE") {
        const parking = getAdditionalBookings(booking, Object.keys(parkingTypes))?.[0];
        let showParking;
        if (parking) {
          this.isParkingSignedIn = parking.requestStatus === 'SIGNED_IN'
          showParking = parking?.typeWiseWaitingList !== null ? !(Object.keys(parking?.typeWiseWaitingList)?.length > 0) : true;
        } else {
          showParking = false;
        }
        if (parking && showParking) {
          loginCommute = {
            id: "parking",
            disable: false,
            value: { premiseId: parking.premiseId, premise: parking.premiseName, seatId: parking?.tokenId, seatName: parking?.tokenName }
          };
          regNo = parking?.extras?.registrationNumber;
        } else if (parking && parking?.typeWiseWaitingList && Object.keys(parking?.typeWiseWaitingList)?.length > 0) {
          loginCommute = {
            id: "parkingWaitlist",
            disable: false,
            value: parking.waitingPremises
          };
        }
      }
    }
    this.bookingForm.get("commute").patchValue({ login: loginCommute, logout: logoutCommute, registrationNumber: regNo });
  }

  onShiftChange(type: string | "login" | "logout", shift: NsEmpWebExpData.IShiftData) {
    if (type === "login") {
      this.bookingForm.get("startShift").patchValue(shift);
      const end = this.bookingForm.get("endShift");
      if (end.value?.timeInMinutes <= shift?.timeInMinutes) {
        this.bookingForm.get("endShift").reset();
      }
    } else if (type === "logout") {
      this.bookingForm.get("endShift").patchValue(shift);
    }
    const commute = this.bookingForm.get("commute");
    if (commute?.value?.[type]?.id === "transport" && !shift?.transportAvalability) {
      commute.patchValue({ [type]: undefined });
    }
    if (this.bookingForm?.value?.startShift && this.bookingForm?.value?.endShift && this.formConfigs.type !== "WFH") {
      this.parkingLoading = !!this.preferenceData.parking?.premiseId && this.formConfigs.type === "OFFICE";
      this.updateSeat();
      this.formConfigs.userConfigs.flattenedSettings.showParking && this.preferenceData.parking && this.updateParking();
      this.formConfigs.userConfigs.flattenedSettings.mealPlanningEnabled && this.preferenceData.meals && this.updateMeal();
      this.parkingLoading = false;
    }
  }

  checkShiftProperties([start, end]): boolean {
    return start?.hours && start?.minutes && end?.hours && end?.minutes ? true : false;
  }

  private updateMeal(): void {
    if (!this.availableAddons.find(a => a.id === "MEAL") || this.formConfigs.mode !== "CREATE") return;
    if (this.preferenceData?.meals) {
      this.getHoursAndMiutes(this.bookingForm, true);
      this.processMealPreferences();
      this.bookingForm.patchValue({ meal: [this.preferenceData.meals] });
      // this.preferenceData?.meals?.mealData?.length === 0 && this.bookingForm.patchValue({ meal: undefined });
    }
  }

  private updateParking(): void {
    if (this.bookingForm.value.commute?.login?.id === "parkingWaitlist") {
      this.bookingForm.value.commute.login.value = [];
    }
    if (!this.availableAddons.find(a => a.id === "COMMUTE") || this.formConfigs.mode !== "CREATE") return;
    const { startDate, endDate, office, commute, startShift, endShift } = this.bookingForm.value;
    const start = tz(startDate, this.dateFormat, office.timezone).add(startShift?.timeInMinutes, "minutes");
    const end = tz(endDate, this.dateFormat, office.timezone).add(endShift?.timeInMinutes, "minutes");
    if (startDate && endDate && this.bookingForm.get("bookingRequestType").value === "OFFICE") {
      if (commute && startShift && endShift) {
        this.preferenceSvc.refreshReplayStreams({
          startTime: start?.valueOf().toString(),
          officeGuid: office.guid,
          bookingType: this.formConfigs.type
        });
        this.preferenceSvc.otherDayPreference.pipe(take(1)).subscribe(
          res => {
            this.preferenceData = res;
            if (this.preferenceData?.parking?.tokenId) {
              this.apiService
                .getSeatAvailablity({
                  seatId: this.preferenceData?.parking?.tokenId,
                  startTime: start?.valueOf().toString(),
                  endTime: end?.valueOf().toString(),
                  officeGuid: office.guid
                })
                .subscribe(parkingRes => {
                  if (parkingRes?.errorCode === 200) {
                    this.setCommute(true, true);
                    this.bookingForm.patchValue({
                      commute: {
                        id: "parking",
                        disable: false,
                        value: {
                          parentPremiseId: this.preferenceData?.parking?.premiseId,
                          premiseId: this.preferenceData?.parking?.premiseId,
                          premise: this.preferenceData?.parking?.premiseName,
                          seatId: this.preferenceData?.parking?.tokenId,
                          seatName: this.preferenceData?.parking?.tokenName,
                          premiseType: this.preferenceData?.parking?.parkingType
                        }
                      }
                    });
                    this.parkingLoading = false;
                  } else {
                    if (this.formConfigs.userConfigs.flattenedSettings.autoSlotAllocate) {
                      this.setCommute(true, false);
                      this.bookingForm.patchValue({
                        commute: {
                          id: "parking",
                          disable: false,
                          value: {
                            parentPremiseId: this.preferenceData?.parking?.premiseId,
                            premiseId: this.preferenceData?.parking?.premiseId,
                            premise: this.preferenceData?.parking?.premiseName,
                            seatName: null,
                            premiseType: this.preferenceData?.parking?.parkingType
                          }
                        }
                      });
                      this.parkingLoading = false;
                    }
                  }
                  if (parkingRes?.errorCode !== 200) {
                    this.bookingForm.get("commute").patchValue({ login: undefined, logout: undefined, registrationNumber: undefined });
                    this.parkingLoading = false;
                  }
                });
            } else {
              if (this.formConfigs.userConfigs.flattenedSettings.autoSlotAllocate && !this.preferenceData.parking?.tokenId) {
                this.setCommute(true, true);
                this.bookingForm.patchValue({
                  commute: {
                    id: "parking",
                    disable: false,
                    value: {
                      parentPremiseId: this.preferenceData?.parking?.premiseId,
                      premiseId: this.preferenceData?.parking?.premiseId,
                      premise: this.preferenceData?.parking?.premiseName,
                      seatId: this.preferenceData?.parking?.tokenId,
                      seatName: null,
                      premiseType: this.preferenceData?.parking?.parkingType
                    }
                  }
                });
                this.parkingLoading = false;
              } else if (
                !this.formConfigs.userConfigs.flattenedSettings.autoSlotAllocate &&
                !this.preferenceData.parking?.tokenId &&
                this.formConfigs.userConfigs.flattenedSettings.enableGridFloorPlan
              ) {
                this.bookingForm.get("commute").patchValue({ login: undefined, logout: undefined, registrationNumber: undefined });
                this.parkingLoading = false;
              }
            }
            this.bookingForm.get("commute").value.login.id === "parking" &&
              !this.parkingDisclaimerAck &&
              this.bookingModalEnabled &&
              this.showParkingDisclaimer();
          },
          err => {
            this.loader = false;
            this.toasts.displayMsg("Failed to fetch preferences.");
          }
        );
      }
    }
  }

  private showParkingDisclaimer(addon?: any, addonExpansion?: boolean): void {
    if (
      this.formConfigs.mode === "EDIT" &&
      (this.bookingForm.get("commute")?.value?.login?.id === "parking" || this.bookingForm.get("commute")?.value?.login?.id === "parkingWaitlist")
    ) {
      this.addonConfigurations = addon;
      this.updateAddon(addon);
      this.expand.emit({
        addon,
        shiftValid: this.bookingForm.get("startShift").valid && this.bookingForm.get("endShift").valid
      });
      this.drawerExpanded = true;
      this.parkingDisclaimerAck = true;
      return;
    }
    const initialState = {
      ignoreBackdropClick: true,
      configs: this.formConfigs.userConfigs
    };
    const modalRef = this.modals.show(ParkingDisclaimerComponent, { data: initialState });
    modalRef.afterClosed.subscribe(response => {
      this.parkingDisclaimerAck = true;
      if (addonExpansion) {
        this.addonConfigurations = addon;
        this.updateAddon(addon);
        this.expand.emit({
          addon,
          shiftValid: this.bookingForm.get("startShift").valid && this.bookingForm.get("endShift").valid
        });
        this.drawerExpanded = true;
      }
    });
  }

  private updateSeat(): void {
    if (!this.availableAddons.find(a => a.id === "SEAT") || this.formConfigs.mode !== "CREATE") return;
    const startDate = this.bookingForm.get("startDate").value;
    const endDate = this.bookingForm.get("endDate").value;
    const seatInfo = this.bookingForm.get("seat").value;
    const officeInfo = this.bookingForm.get("office").value;
    const startShift = this.bookingForm.get("startShift").value;
    const endShift = this.bookingForm.get("endShift").value;
    const start = tz(startDate, this.dateFormat, officeInfo.timezone).add(startShift?.timeInMinutes, "minutes");
    if (startDate && endDate && this.bookingForm.get("bookingRequestType").value === "OFFICE") {
      // if seat is selected, check availability, else fetch preference availability based on selected shift timings
      if (seatInfo && seatInfo?.seatId && this.checkShiftProperties([startShift, endShift])) {
        this.apiService
          .getSeatAvailablity({
            seatId: seatInfo.seatId,
            startTime: parseZone(startDate, this.dateFormat)
              .tz(this.bookingSvc?.timezone)
              .set("hour", startShift?.hours)
              .set("minute", startShift?.minutes)
              .set("second", 0)
              .valueOf(),
            endTime: parseZone(endDate, this.dateFormat)
              .tz(this.bookingSvc?.timezone)
              .set("hour", endShift?.hours)
              .set("minute", endShift?.minutes)
              .set("second", 0)
              .valueOf(),
            officeGuid: officeInfo.guid
          })
          .subscribe(res => {
            if (res?.errorCode === 200) return;
            if (res?.message) {
              this.toasts.displayMsg(res.message, 4000);
              this.bookingForm.patchValue({ seat: undefined });
            }
          });
      } else if (startShift && endShift) {
        this.preferenceSvc.refreshReplayStreams({
          startTime: start?.valueOf().toString(),
          officeGuid: officeInfo.guid,
          bookingType: this.formConfigs.type
        });
        this.loader = true;
        this.preferenceSvc.otherDayPreference.pipe(take(1)).subscribe(
          res => {
            this.loader = false;
            this.preferenceData = res;
            this.preferenceData?.seatDetails?.seatId && this.bookingForm.patchValue({ seat: res.seatDetails });
            this.seatSvc.changeSeat(res.seatDetails);
            this.allocatedSeatId = !!res?.seatDetails?.seatId ? res?.seatDetails?.seatId : "";
          },
          err => {
            this.loader = false;
            this.toasts.displayMsg("Failed to fetch preferences.");
          }
        );
      }
    }
    seatInfo && this.seatSvc.changeSeat(seatInfo);
  }
  handleCommuteChange([login, logout, registrationNumber, entityTypeId, vehicleId]: [
    NsEmpWebExpData.ICommuteOption?,
    NsEmpWebExpData.ICommuteOption?,
    string?,
    string?,
    string?
  ]): void {
    if (login?.id === "parkingWaitlist" && login?.value?.length === 0) {
      login = undefined;
    }
    this.bookingForm.get("commute").patchValue({ login, logout, registrationNumber, entityTypeId, vehicleId });
  }
  handleMealChange(data: NsEmpWebExpData.IMeal[]): void {
    if (data.length > 0) {
      let mapMeal = new Map<string, NsEmpWebExpData.IMealsEntity>();
      data?.[0]?.mealData.map(item => mapMeal.set(item?.mealId, item));
      data[0].mealMap = mapMeal;
    }
    this.bookingForm.patchValue({ meal: data });
  }

  saveBooking(): void {
    let commute = this.bookingForm.get("commute");
    let isParkingCommute = commute?.value.login?.id === "parking" || commute?.value.login?.id === "parkingWaitlist";
    let invalidVehicleNumber = commute?.value.registrationNumber === undefined || commute?.value.registrationNumber?.trim().length == 0;
    if (
      this.formConfigs.userConfigs.flattenedSettings.showParking &&
      this.formConfigs.userConfigs.flattenedSettings.showRegistrationNumberInputFieldForParking &&
      isParkingCommute &&
      invalidVehicleNumber
    ) {
      this.toasts.displayMsg("Vehicle registration number is mandatory!");
      return;
    }
    let channel = this.configSvc.isTeamsApp ? "MS_TEAMS" : "EMPLOYEE_WEB";
    if (this.formConfigs.mode === "EDIT" || this.formConfigs.mode === "CONVERT") {
      const req = editBookingRequest(this.formConfigs.profile, this.bookingForm, this.formConfigs.data, channel, this.parkingDisclaimerAck);
      this.update.emit(req);
    } else if (this.formConfigs.mode === "CREATE") {
      this.formConfigs.type === "OFFICE" && this.bookingForm.patchValue({ reason: this.bookingReasons?.[0] });
      const req = createBookingRequest(
        this.formConfigs.profile,
        this.bookingForm,
        this.datesDisabled,
        this.dateFormat,
        this.recurrenceBookingEnabled,
        this.formConfigs.userConfigs.flattenedSettings.enableWeeklyOffBookings,
        this.parkingDisclaimerAck,
        channel
      );
      if (this.recurrenceBookingEnabled && this.bookingForm.get("recurrence").value.recurrenceVisibility) {
        // const ref = this.modals.show(RecurrenceConfirmationComponent, null, { closeOnBackdropClick: false });
        // ref.afterClosed.subscribe(res => {
        //   if (res) {
        //     this.create.emit([req, true]);
        //   }
        // });
        this.create.emit([req, true]);
      } else {
        if (this.formConfigs.type === "OFFICE") {
          this.analytics.send("emp_web_wfo_confirm", "emp_web", "click", "");
        }
        if (this.formConfigs.type === "WFH") {
          this.analytics.send("emp_web_wfh_confirm", "emp_web", "click", "");
        }
        if (!!this.allocatedSeatId && !!this.bookingForm.get("seat")?.value?.seatId) {
          if (this.allocatedSeatId === this.bookingForm.get("seat")?.value?.seatId) {
            this.analytics.send(
              "booking_allocated_desk",
              "emp_web",
              "click",
              this.bookingForm.get("seat")?.value?.seatId.toLowerCase().replace(/\s/g, "_")
            );
          } else {
            this.analytics.send(
              "booking_after_allocated_desk",
              "emp_web",
              "click",
              this.bookingForm.get("seat")?.value?.seatId.toLowerCase().replace(/\s/g, "_")
            );
          }
        }
        this.create.emit([req, false]);
      }
    }
  }

  setBookingReasons(booking?: NsEmpWebExpData.IBookingDetailDTOS) {
    const typeOfBooking = this.formConfigs.type === "OFFICE" ? "wfoReasonList" : "wfhReasonList";
    return this.apiService.getBookingReasonList(typeOfBooking).subscribe(bookingReason => {
      this.bookingReasons = bookingReason;
      if (booking?.bookingReason) {
        const foundReason = this.bookingReasons.find(reason => reason === booking.bookingReason);
        if (!foundReason) {
          this.bookingForm.get("inputText")?.enable();
          this.bookingForm.patchValue({ reason: this.other, inputText: booking.bookingReason });
        } else {
          this.bookingForm.patchValue({ reason: booking.bookingReason });
        }
      }
    });
  }

  removeAddon(opt: NsEmpWebExpData.IAddon, data?: NsEmpWebExpData.IMealsEntity, index?: number): void {
    if (
      (opt.id === "SEAT" && this.disableOfficeChange && this.formConfigs?.data?.requestStatus === "SIGNED_IN") ||
      (opt.id === "MEAL" && !data.mealCutOff) || 
      (opt.id === 'COMMUTE' &&this.isParkingSignedIn)
    ) {
      return;
    }
    switch (opt.id) {
      case "SEAT":
        this.bookingForm.patchValue({ seat: undefined });
        this.seatSvc.temperarySeat.next(undefined);
        this.seatSvc.changeSeat(undefined);
        this.bookingForm.patchValue({ meal: undefined });
        this.drawerExpanded && this.updateAddon(opt);
        break;
      case "COMMUTE":
        if (
          this.formConfigs.userConfigs.flattenedSettings.enableWaitlistBooking &&
          this.bookingForm.get("commute").value?.login?.id === "parkingWaitlist"
        ) {
          this.commuteSvc.removeWaitlistedParking(this.bookingForm?.value?.commute?.login?.value?.find((val, i) => i === index));
          if (!this.drawerExpanded || this.commuteSvc.enableGridFloorPlan) {
            const premises = JSON.parse(JSON.stringify(this.bookingForm.value.commute.login.value));
            premises.splice(index, 1);
            premises.length > 0
              ? this.bookingForm.get("commute").patchValue({ login: { id: "parkingWaitlist", value: premises, disabled: false } })
              : this.bookingForm.get("commute").patchValue({ login: undefined });
          }
        } else {
          this.bookingForm.get("commute").reset();
          this.commuteSvc.temporaryCommuteSelection = [];
          this.commuteSvc.setAllotedParkingData(null);
          this.drawerExpanded && this.updateAddon(opt);
        }
        break;
      case "MEAL":
        let meal = this.bookingForm.value.meal;
        for (let i = 0; i < meal.length; i++) {
          const checkId = meal[i].mealMap.get(data.mealId);
          if (checkId)
            meal.map(element => {
              const index = element.mealData.findIndex(id => id.mealId === checkId.mealId);
              element.mealMap.delete(checkId.mealId);
              if (index > -1) element.mealData.splice(index, 1);
            });
        }
        if (meal[0].mealData.length === 0) {
          meal = [];
        }
        if (meal.length > 0) this.bookingForm.patchValue({ meal: meal });
        else this.bookingForm.patchValue({ meal: undefined });
        this.mealService.changedMealData(meal);
        break;
      case "OTHER_DETAILS":
        this.dynamicFields = undefined;
        this.bookingForm.patchValue({ specialRequests: undefined });
        this.bookingForm.patchValue({ dynamicSpecialRequests: undefined });
        if (this.formConfigs && this.formConfigs.data && this.formConfigs.data.extras) {
          this.formValues = {};
        }
        this.drawerExpanded && this.updateAddon(opt);
        break;
    }
  }

  //Dynamic form values
  updatedForm(e) {
    this.formValues = e;
    this.bookingForm.patchValue({ specialRequests: e });
    this.modifyUpdatedFormValues(e);
  }

  modifyUpdatedFormValues(e) {
    this.dynamicFields.map(x => {
      if (x.subFields) {
        for (const key in e) {
          if (e.hasOwnProperty(key)) {
            if (key === x.configName) {
              if (e[x.configName]?.subFields) {
                for (const i in e[x.configName]?.subFields) {
                  if (e[x.configName]?.subFields.hasOwnProperty(i)) {
                    e[i] = e[x.configName]?.subFields[i];
                  }
                }
              }
              const output = e[x.configName]?.value ? [e[x.configName]?.value] : [e[x.configName]];
              e[x.configName] = output;
            }
          }
        }
      }
    });
  }

  validate(event) {
    this.isDynamicFormValid = event;
  }
}
